import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../context/AuthContext';
import supabase from '../lib/supabase';
import '../styles/neumorphism.css';

const { FiUser, FiLock, FiEye, FiEyeOff, FiLoader, FiMail, FiCheck, FiAlertTriangle } = FiIcons;

function Login() {
  const { login, loading, error } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Password Reset State
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetStatus, setResetStatus] = useState(null); // 'sending', 'success', 'error'

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      return;
    }
    setIsSubmitting(true);
    try {
      await login(formData.email, formData.password);
    } catch (error) {
      console.error('Login error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail) return;
    setResetStatus('sending');
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: window.location.origin + '/reset-password',
      });
      if (error) throw error;
      setResetStatus('success');
    } catch (error) {
      console.error('Password reset error:', error);
      setResetStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full neu-element flex items-center justify-center">
            <img src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1759820326245-icon%20transparent.png" alt="HelmPlanner Logo" className="w-12 h-12 object-contain" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900">HelmPlanner</h2>
          <p className="mt-2 text-sm text-gray-600">SGM Dienstplangenerator</p>
          <p className="mt-4 text-lg font-medium text-gray-800">
            {showForgotPassword ? 'Passwort zurücksetzen' : 'Anmeldung erforderlich'}
          </p>
        </div>
        <div className="p-8 neu-card">
          {showForgotPassword ? (
            <form className="space-y-6" onSubmit={handleResetPassword}>
              {resetStatus === 'success' ? (
                <div className="text-center">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                    <SafeIcon icon={FiCheck} className="h-6 w-6 text-green-600" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900">E-Mail gesendet!</h3>
                  <p className="mt-2 text-sm text-gray-500">
                    Wir haben Ihnen einen Link zum Zurücksetzen Ihres Passworts an <strong>{resetEmail}</strong> gesendet.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setResetStatus(null);
                      setResetEmail('');
                    }}
                    className="mt-6 w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    Zurück zur Anmeldung
                  </button>
                </div>
              ) : (
                <>
                  {resetStatus === 'error' && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg neu-element flex items-center text-sm text-red-600">
                      <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 mr-2" />
                      Fehler beim Senden. Bitte prüfen Sie die E-Mail-Adresse.
                    </div>
                  )}
                  <div>
                    <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700 mb-2">
                      E-Mail-Adresse
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <SafeIcon icon={FiMail} className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        id="reset-email"
                        type="email"
                        required
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        className="neu-input w-full pl-10"
                        placeholder="ihre.email@example.com"
                      />
                    </div>
                    <p className="mt-2 text-xs text-gray-500">
                      Sie erhalten einen Link per E-Mail, um ein neues Passwort zu vergeben.
                    </p>
                  </div>
                  <div>
                    <button
                      type="submit"
                      disabled={resetStatus === 'sending' || !resetEmail}
                      className="w-full flex justify-center items-center py-3 px-4 neu-button bg-orange-50 text-orange-700 font-medium disabled:opacity-50"
                    >
                      {resetStatus === 'sending' ? (
                        <>
                          <SafeIcon icon={FiLoader} className="w-5 h-5 mr-2 animate-spin" />
                          Sende...
                        </>
                      ) : (
                        'Link senden'
                      )}
                    </button>
                  </div>
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(false)}
                      className="text-sm text-gray-600 hover:text-gray-900"
                    >
                      Zurück zur Anmeldung
                    </button>
                  </div>
                </>
              )}
            </form>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit}>
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg neu-element">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  E-Mail-Adresse
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <SafeIcon icon={FiUser} className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="neu-input w-full pl-10"
                    placeholder="ihre.email@example.com"
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    Passwort
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-xs text-orange-600 hover:text-orange-800"
                  >
                    Passwort vergessen?
                  </button>
                </div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <SafeIcon icon={FiLock} className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className="neu-input w-full pl-10 pr-10"
                    placeholder="Ihr Passwort"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <SafeIcon icon={showPassword ? FiEyeOff : FiEye} className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  </button>
                </div>
              </div>
              <div>
                <button
                  type="submit"
                  disabled={isSubmitting || loading || !formData.email || !formData.password}
                  className="w-full flex justify-center items-center py-3 px-4 neu-button bg-orange-50 text-orange-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting || loading ? (
                    <>
                      <SafeIcon icon={FiLoader} className="w-5 h-5 mr-2 animate-spin" />
                      Anmeldung läuft...
                    </>
                  ) : (
                    'Anmelden'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-500">© {new Date().getFullYear()} by Achim Helm. Alle Rechte vorbehalten.</p>
        </div>
      </div>
    </div>
  );
}

export default Login;